import React, { useState } from "react";
import { AlertIcon, ThumbUpIcon, ThumbDownIcon } from "../icons";

/**
 * Renders a ChatMS answer with its trust signals.
 *
 * Billers act on money. If they can't see where a number came from they won't trust it,
 * and an assistant nobody trusts is worse than none. So the citations, the provenance
 * strip and the deep links are not decoration - they are the feature.
 */

/* --------------------------------------------------------------- citations */

function RefChip({ n, citation, open, onToggle }) {
  return (
    <button
      type="button"
      className={`remi-ref ${open ? "is-open" : ""}`}
      onClick={() => onToggle(n)}
      title={citation.sTitle || `Source ${n}`}
      aria-label={`Source ${n}: ${citation.sTitle || ""}`}
    >
      {n}
    </button>
  );
}

/* ---------------------------------------------------------------- markdown */

/**
 * A small markdown renderer, scoped to what ChatMS actually emits.
 *
 * This is not cosmetic. The system prompt tells the model to "use a short markdown table when
 * listing more than about three claims" - the most common billing question there is - and
 * remi.css has carried table, heading, code and blockquote styling from the start. Only the
 * renderer was missing, so a claims list reached the biller as a wall of raw pipes and dashes.
 *
 * Deliberately not a markdown library. This renders PHI-bearing text inside the billing portal,
 * so it handles exactly the constructs the service is known to produce and lets anything else
 * fall through as literal text rather than interpreting it. Raw HTML is never parsed.
 *
 * Underscore emphasis (_x_, __x__) is intentionally NOT supported: billing text is full of
 * identifiers like Claim_ID and CO_197, and treating those as italics would corrupt the data
 * the biller is reading.
 */

/** Inline spans, longest-first so ** is matched before *, and code stays opaque. */
const INLINE_PATTERN = /(`[^`\n]+`|\*\*[^*\n]+\*\*|\*[^*\n]+\*|https?:\/\/[^\s)]+|\[ref:\d+\])/g;

function renderInline(value, keyPrefix, citations, openRef, toggleRef) {
  return String(value ?? "")
    .split(INLINE_PATTERN)
    .map((piece, index) => {
      if (!piece) return null;
      const key = `${keyPrefix}-i${index}`;

      // A [ref:N] the payload carries no citation for is dropped rather than shown raw -
      // an orphan tag in the prose reads as a fault in the answer itself.
      const ref = piece.match(/^\[ref:(\d+)\]$/);
      if (ref) {
        const n = Number(ref[1]);
        const citation = citations[n - 1];
        if (!citation) return null;
        return (
          <RefChip key={key} n={n} citation={citation} open={openRef === n} onToggle={toggleRef} />
        );
      }

      if (/^https?:\/\//i.test(piece)) {
        return (
          <a key={key} href={piece} target="_blank" rel="noopener noreferrer nofollow">
            {piece}
          </a>
        );
      }

      if (piece.startsWith("`")) return <code key={key}>{piece.slice(1, -1)}</code>;
      if (piece.startsWith("**")) return <strong key={key}>{piece.slice(2, -2)}</strong>;
      if (piece.startsWith("*")) return <em key={key}>{piece.slice(1, -1)}</em>;

      return piece;
    });
}

const RE_FENCE = /^\s*```/;
const RE_TABLE_ROW = /^\s*\|/;
const RE_HEADING = /^\s*(#{1,4})\s+(.*)$/;
const RE_QUOTE = /^\s*>\s?/;
const RE_BULLET = /^\s*[-*+]\s+/;
const RE_ORDERED = /^\s*\d+[.)]\s+/;

/** A |---|:--:|---| divider: pipes, dashes, colons and spaces only, with at least one dash. */
function isTableDivider(line) {
  return line.includes("|") && line.includes("-") && /^[\s|:-]+$/.test(line);
}

function splitCells(line) {
  return line
    .replace(/^\s*\|/, "")
    .replace(/\|\s*$/, "")
    .split("|")
    .map((cell) => cell.trim());
}

function startsNewBlock(line) {
  return (
    RE_FENCE.test(line) ||
    RE_TABLE_ROW.test(line) ||
    RE_HEADING.test(line) ||
    RE_QUOTE.test(line) ||
    RE_BULLET.test(line) ||
    RE_ORDERED.test(line)
  );
}

function renderBlocks(text, citations, openRef, toggleRef) {
  const lines = String(text ?? "").replace(/\r\n/g, "\n").split("\n");
  const out = [];
  const inline = (value, key) => renderInline(value, key, citations, openRef, toggleRef);
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];
    const key = `b${out.length}`;

    if (!line.trim()) {
      i += 1;
      continue;
    }

    // Fenced code. An unterminated fence runs to the end rather than swallowing the answer.
    if (RE_FENCE.test(line)) {
      const body = [];
      i += 1;
      while (i < lines.length && !RE_FENCE.test(lines[i])) {
        body.push(lines[i]);
        i += 1;
      }
      i += 1;
      out.push(
        <pre key={key}>
          <code>{body.join("\n")}</code>
        </pre>
      );
      continue;
    }

    // Table: a pipe row immediately followed by a divider. Without the divider it is just
    // prose that happens to contain a pipe, and must not be reflowed into a grid.
    if (RE_TABLE_ROW.test(line) && i + 1 < lines.length && isTableDivider(lines[i + 1])) {
      const header = splitCells(line);
      i += 2;
      const rows = [];
      while (i < lines.length && RE_TABLE_ROW.test(lines[i])) {
        rows.push(splitCells(lines[i]));
        i += 1;
      }
      out.push(
        <div className="remi-md-tablewrap" key={key}>
          <table>
            <thead>
              <tr>
                {header.map((cell, ci) => (
                  <th key={`${key}h${ci}`}>{inline(cell, `${key}h${ci}`)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, ri) => (
                <tr key={`${key}r${ri}`}>
                  {/* Driven by the header, so a short or over-long row can't shift columns
                      and silently print one claim's figure under another claim's heading. */}
                  {header.map((_, ci) => (
                    <td key={`${key}r${ri}c${ci}`}>{inline(row[ci] ?? "", `${key}r${ri}c${ci}`)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      continue;
    }

    const heading = line.match(RE_HEADING);
    if (heading) {
      const Tag = `h${heading[1].length}`;
      out.push(<Tag key={key}>{inline(heading[2], key)}</Tag>);
      i += 1;
      continue;
    }

    if (RE_QUOTE.test(line)) {
      const body = [];
      while (i < lines.length && RE_QUOTE.test(lines[i])) {
        body.push(lines[i].replace(RE_QUOTE, ""));
        i += 1;
      }
      out.push(<blockquote key={key}>{inline(body.join(" "), key)}</blockquote>);
      continue;
    }

    if (RE_BULLET.test(line) || RE_ORDERED.test(line)) {
      const bOrdered = RE_ORDERED.test(line) && !RE_BULLET.test(line);
      const marker = bOrdered ? RE_ORDERED : RE_BULLET;
      const items = [];
      while (i < lines.length && marker.test(lines[i])) {
        items.push(lines[i].replace(marker, ""));
        i += 1;
      }
      const Tag = bOrdered ? "ol" : "ul";
      out.push(
        <Tag key={key}>
          {items.map((item, ii) => (
            <li key={`${key}l${ii}`}>{inline(item, `${key}l${ii}`)}</li>
          ))}
        </Tag>
      );
      continue;
    }

    // Paragraph: runs until a blank line or the start of another block.
    //
    // The current line is taken unconditionally before the loop, which is what guarantees the
    // cursor always advances. A line can reach here while startsNewBlock() still reports true
    // for it - a pipe row whose next line is not a divider is the real case - and testing the
    // condition first would then consume nothing and spin forever on that line.
    const para = [line];
    i += 1;
    while (i < lines.length && lines[i].trim() && !startsNewBlock(lines[i])) {
      para.push(lines[i]);
      i += 1;
    }
    out.push(
      <p key={key}>
        {para.map((paraLine, li) => (
          <React.Fragment key={`${key}p${li}`}>
            {li > 0 && <br />}
            {inline(paraLine, `${key}p${li}`)}
          </React.Fragment>
        ))}
      </p>
    );
  }

  return out;
}

function AnswerBody({ text, citations, openRef, toggleRef }) {
  return <div className="remi-md">{renderBlocks(text, citations, openRef, toggleRef)}</div>;
}

/* ------------------------------------------------------------- provenance */

function ProvenanceStrip({ sources }) {
  if (!sources?.length) return null;
  return (
    <div className="remi-sources">
      {sources.map((s, i) => (
        <div
          key={`${s.sTool || s.sEndpoint}-${i}`}
          className={`remi-source ${s.bSucceeded === false ? "is-failed" : ""}`}
        >
          {s.bSucceeded === false && <AlertIcon size={12} />}
          <span className="remi-source-ep">{s.sEndpoint || s.sTool}</span>
          {typeof s.iRowCount === "number" && (
            <>
              <span className="remi-dot">·</span>
              <span>{s.iRowCount === 1 ? "1 row" : `${s.iRowCount} rows`}</span>
            </>
          )}
          {s.sRetrievedAt && (
            <>
              <span className="remi-dot">·</span>
              <span>{s.sRetrievedAt}</span>
            </>
          )}
        </div>
      ))}
    </div>
  );
}

/* -------------------------------------------------------------- deep links */

function DeepLinks({ links }) {
  if (!links?.length) return null;
  return (
    <div className="remi-deeplinks">
      {links.map((l, i) => (
        // The portal uses HashRouter, so routes must be prefixed with #.
        <a key={`${l.sRoute}-${i}`} className="remi-deeplink" href={`#${l.sRoute}`}>
          {l.sLabel}
        </a>
      ))}
    </div>
  );
}

/* ---------------------------------------------------------------- message */

function AssistantMessage({ message, onFeedback }) {
  const [openRef, setOpenRef] = useState(null);
  const toggleRef = (n) => setOpenRef((cur) => (cur === n ? null : n));

  if (message.status === "pending") {
    return (
      <div className="remi-bubble remi-bubble-assistant">
        <div className="remi-working">
          <span className="remi-typing">
            <span />
            <span />
            <span />
          </span>
          <span className="remi-working-label">{message.statusLabel}</span>
        </div>
      </div>
    );
  }

  if (message.status === "error") {
    return (
      <div className="remi-bubble remi-bubble-assistant remi-bubble-error">
        <div className="remi-bubble-error-head">
          <AlertIcon size={15} />
          <span>{message.errorKind === "unavailable" ? "Assistant unavailable" : "Couldn't answer that"}</span>
        </div>
        <div>{message.text}</div>
      </div>
    );
  }

  const data = message.data || {};
  const citations = data.Citations || [];
  const openCitation = openRef ? citations[openRef - 1] : null;

  return (
    <div className="remi-bubble remi-bubble-assistant">
      {data.bToolLimitReached && (
        <div className="remi-partial">
          <AlertIcon size={13} />
          <span>
            Partial answer — try narrowing to a specific claim or a shorter date range.
          </span>
        </div>
      )}

      {/* The answer stopped at the model's length limit, so it is a fragment. Shown even
          though the text says so too: a cut-off figure must never look like a conclusion. */}
      {data.bAnswerTruncated && (
        <div className="remi-partial">
          <AlertIcon size={13} />
          <span>
            Cut off before finishing — treat the figures below as incomplete and verify on screen.
          </span>
        </div>
      )}

      <AnswerBody
        text={data.sAnswer}
        citations={citations}
        openRef={openRef}
        toggleRef={toggleRef}
      />

      {openCitation && (
        <div className="remi-citation-panel">
          <div className="remi-citation-head">
            <strong>{openCitation.sTitle || `Source ${openRef}`}</strong>
            {openCitation.sDocType && <span className="remi-citation-type">{openCitation.sDocType}</span>}
          </div>
          {openCitation.sSnippet && <div className="remi-citation-snippet">{openCitation.sSnippet}</div>}
        </div>
      )}

      <DeepLinks links={data.DeepLinks} />
      <ProvenanceStrip sources={data.DataSources} />

      {data.sAsOf && <div className="remi-asof">As of {data.sAsOf}</div>}

      {data.MessageID && (
        <div className="remi-msg-actions">
          <button
            type="button"
            className={`remi-icon-btn ${message.feedback === 1 ? "is-active" : ""}`}
            onClick={() => onFeedback(message, 1)}
            title="Helpful"
            aria-label="Mark answer as helpful"
          >
            <ThumbUpIcon />
          </button>
          <button
            type="button"
            className={`remi-icon-btn ${message.feedback === -1 ? "is-active" : ""}`}
            onClick={() => onFeedback(message, -1)}
            title="Not helpful"
            aria-label="Mark answer as not helpful"
          >
            <ThumbDownIcon />
          </button>
        </div>
      )}
    </div>
  );
}

export default function MessageList({ messages, onFeedback }) {
  return (
    <>
      {messages.map((message) => (
        <div
          key={message.id}
          className={`remi-msg ${message.role === "user" ? "remi-msg-user" : "remi-msg-assistant"}`}
        >
          {message.role === "user" ? (
            <div className="remi-bubble remi-bubble-user">{message.text}</div>
          ) : (
            <AssistantMessage message={message} onFeedback={onFeedback} />
          )}
        </div>
      ))}
    </>
  );
}
